import { WalletContent } from "./wallet-custody";

export enum DLTProtocolEnum {
  Ethererum = "eth",
  Tezos = "tez",
  Polygon = "matic"
}

export type RawData = Buffer;
export type TxSignature = Buffer;
export type TransactionId = string;

export interface TransactionSigner {
  sign(w: WalletDLT, tx: RawData): Promise<TxSignature>;
}

export interface WalletNoncesFields {
  asOfBlock: number;
  lastInBlock: bigint;
  lastPending: bigint;
  lastReserved: bigint;
}
// we use these as constant to avoid creating new BigInt each time and because TS2737: BigInt literals are not available when targeting lower than ES2020.
const BI_0 = BigInt(0);
const BI_1 = BigInt(1);
/**
 * The wallet nonces logic need to remain consistent accross the stack
 * asOfBlock is the block number of the last known block
 * lastInBlock is the last nonce used in the last known block
 * lastPending is the last nonce used in the pending transactions when available
 * lastReserved is the last nonce reserved for a transaction if any as saved in cache
 * reservation is a flag to indicate if the WalletNonces is returned for a reserveTransactionCounter call
 * next is the next nonce to use, when after a call to reserveTransactionCounter, it is the actually last reserved nonce
 * 
 * The idea with next it to get the api user always use next to fill the nonce field of the transaction
 * This class is implementing the next logic so it remains consistent accross the stack
 */
export class WalletNonces {
  static from(fields: Partial<WalletNoncesFields>, asReservation=false): WalletNonces {
    const n = new WalletNonces();
    n.asOfBlock = fields.asOfBlock;
    n.lastInBlock = fields.lastInBlock;
    n.lastPending = fields.lastPending;
    n.lastReserved = fields.lastReserved;
    n.reservation = asReservation;
    return n;
  }
  asOfBlock: number | undefined;
  lastInBlock: bigint | undefined;
  lastPending: bigint | undefined;
  lastReserved: bigint | undefined;
  reservation: boolean = false;

  get next(): bigint {
    // by default the next nonce is the lastInBlock + 1, or zero if it is the first time we use the wallet
    let next = this.lastInBlock !== undefined? this.lastInBlock + BI_1 : BI_0;
    // if there is pending transactions, they must have a nonce greater than the lastInBlock, and we want the next to be just after the lastPending
    next = this.lastPending !== undefined && this.lastPending > next? this.lastPending + BI_1 : next;
    if (this.reservation)
      // if there are reservation the last reserved nonce is the next (it must exsits but in case not we use the calculated one)
      next = this.lastReserved !== undefined ? this.lastReserved : next;
    else 
      // if there are no reservation, the lastReserved is a previously used one and we want the next to be just after
      next = this.lastReserved !== undefined && this.lastReserved > next? this.lastReserved + BI_1 : next;

    return next;
  }
}
export interface DLTInfo {
  [key: string]: any;
}

export interface WalletDLT {
  get protocol(): DLTProtocolEnum;
  get signer(): TransactionSigner;
  getInfo(): Promise<DLTInfo>;
  getAddress(): string;
  getBalance(): Promise<bigint>;
  getTransactionCounter(reset?: boolean): Promise<WalletNonces>;
  reserveTransactionCounter(reset?: boolean): Promise<WalletNonces>;
}

export type TransactionDLTStatus =
  | "unknown"
  | "created"
  | "initialized"
  | "linked"
  | "priced"
  | "signed"
  | "submitted"
  | "recorded"
  | "failed";

  /** this type should be serializable in DB so no bigint */
export interface TransactionDecoded {
  /** the blockchain transaction id, typically a hash. Optional because not available if not signed */
  txId?: string;
  from: string;
  to?: string;
  nonce: number,
  amount: string; // hex form or decimal form
  data: Buffer;
  gas: string; // hex form or decimal form
  gasPrice: string; // hex form or decimal form
  protocol: DLTProtocolEnum;
  chainId: number;
  r?: string; s?: string; v?: string;
  [key: string]: unknown;
}

export interface TransactionDLTDetails {
  id: string,
  status: TransactionDLTStatus,
  block?: number,
  index?: number,
  from: string,
  to?: string,
  nonce: number,
  amount: bigint,
  data: Buffer,
  fee: bigint,
  gasPrice: bigint,
  contractCreated?: string,
  protocol: DLTProtocolEnum,
  // dlt?: DLTInfo,
  // timeout?: boolean
}

export interface DLTBlockDetails {
  block: number,
  hash: string,
  parentHash?: string,
  time: number,
  nbTransactions: number,
  transactions?: TransactionDLTDetails[]
}


export interface TransactionDLT {
  get id(): TransactionId;
  get status(): TransactionDLTStatus;
  get raw(): Buffer;
  set raw(v: Buffer);
  get details(): TransactionDLTDetails;
  init(to: string, data: RawData, amount: bigint): void;
  link(w: WalletDLT, maxGas: bigint, nonce: bigint): void;
  call(): Promise<string>;
  price(check?: (price: bigint)=>Promise<bigint>): Promise<bigint>;
  sign(): Promise<void>;
  submit(): Promise<TransactionDLT>;
  waitFor(nbBlocks: number, timeout?: number): Promise<TransactionDLT>;
}

export interface DLTNode {
  get protocol(): DLTProtocolEnum;
  getInfo(): Promise<DLTInfo>;
  newTransaction(to?: string, data?: RawData, amount?: bigint): TransactionDLT;
  getTransaction(id: string): Promise<TransactionDLT>;
  getWallet(address: string, signer?: TransactionSigner): WalletDLT;
  getLastBlockNumber(): Promise<number>;
  getBasePrice(): Promise<bigint>;
  getDLTBlockReader(): Promise<DLTBlockReader>;
  close(): Promise<void>;
}

export interface EventLogDetails {
  uniqueId: string;
  block: number;
  transactionIndex: number;
  logIndex: number;
  transactionId: string;
  /** the address of the smart contract creating that log */
  origin: string;
  state: "mined" | "removed";
  data: string;
  topics: string[];
  protocol: DLTProtocolEnum;
}

export function calcEventLogUniqueId(protocol: DLTProtocolEnum, block: number, transaction: number, log: number): string {
  return (
    protocol + ":" +
    String(block).padStart(8, "0") +
    String(transaction).padStart(3, "0") +
    String(log).padStart(3, "0")
  );
}

export interface EventLogSubscriptionFilter {
  origin?: string;
  topics?: (string|string[])[];
}
export interface EventLogSubscriptionInfo extends EventLogSubscriptionFilter {
  id: string;
  protocol: DLTProtocolEnum;
}

export interface EventLogSubscriptionQuery {
  /** id of a created subscription. If specified `filter` can be optional. If missing `filter` is mandatory */
  idOrFilter: string | EventLogSubscriptionFilter;
  fromBlock?: "earliest"|"latest"|number;
  toBlock?: "latest"|number;
  maxItems?: number;
}

export interface EventLogSubscriptionResponse {
  id: string;
  logs: EventLogDetails[];
  /** populated with the query to get the rest, if any */
  more?: EventLogSubscriptionQuery;
}

export interface DLTBlockReader {
  getBlock(block: number, withTransactions: boolean): Promise<DLTBlockDetails | undefined>;
  findFirstBlockOfAddress(address: string): Promise<{ firstBlock: number; transactions: TransactionDLTDetails[] }>;
  buildLogSubscription(filter: EventLogSubscriptionFilter): Promise<EventLogSubscriptionInfo>;
  getLogs(query: EventLogSubscriptionQuery): Promise<EventLogSubscriptionResponse>;
  getLogsAsync(query: EventLogSubscriptionQuery, callback: (error:any, res?: EventLogSubscriptionResponse)=>Promise<void>): Promise<EventLogSubscriptionInfo>;
  clearLogSubscription(filter: EventLogSubscriptionInfo): Promise<EventLogSubscriptionInfo>;
}

export type DecryptWalletFunction = (w: WalletContent) => Promise<WalletContent>;

export interface DLTProtocolServices {
  get protocol(): DLTProtocolEnum;
  generateNewWallet(): Promise<WalletContent>;
  addWallet(privateKey: string): WalletContent;
  getWalletDLT(address: string, info: DLTInfo): Promise<WalletDLT>;
  getSigner(decryptWallet?: DecryptWalletFunction): TransactionSigner;
  sign(privateKey: string, data: Buffer): Promise<Buffer>;
  verify(publicKey: string, data: Buffer, signature: Buffer): Promise<boolean>;
}

export interface DLTProtocolEncoder {
  get protocol(): DLTProtocolEnum;
  from(from: WalletDLT, raw: Buffer): Promise<TransactionDecoded>;
  to(tx: TransactionDecoded): Promise<Buffer>;
}

export interface ContractDefinitionGeneric {
  key: string;
  name: string;
  nbArguments?: number;
  [key: string]: any;
}

export interface ContractDefinitionServices {
  get protocol(): DLTProtocolEnum;
  addFile(file: string | any): Promise<void>;
  list(): Promise<ContractDefinitionGeneric[]>;
}